import React from 'react';
import {useTranslation} from 'react-i18next';
import {effetti} from '../../moduli/leggi';
import {inAnteprima} from '../../skin';
import {impostaSuonoEffetti, suonoEffetti} from '../../preferenze';
import {importo as scriviImporto} from '../../utils/conto';
import {CRIPTO, iconaCripto} from '../Nova/cripto';
import {EVENTO_BONUS, EVENTO_DEPOSITO} from '../../effetti';
import './Effetti.css';

/**
 * GLI EFFETTI (modulo Effetti): la festa quando arriva un versamento o
 * scatta un bonus. Una pioggia di monete che cade sopra la pagina per
 * qualche secondo, un avviso in alto col titolo e l'importo, e un tintinnio.
 *
 * CHI LI FA PARTIRE: nessuno da qui. La Cassa, quando il servizio dei
 * pagamenti dice che il versamento e' accreditato, chiama annunciaDeposito
 * con la moneta e l'importo; il guscio, quando fra i bonus attivi del conto
 * ne compare uno nuovo, chiama annunciaBonus. Sono eventi della finestra
 * (EVENTO_DEPOSITO, EVENTO_BONUS): questo componente li ascolta e basta, e
 * chi li manda non sa se c'e' qualcuno ad ascoltare. Il modulo spento dal
 * backoffice: si ascolta e non si fa niente.
 *
 * LA MONETA E' QUELLA VERSATA: chi manda bitcoin vede cadere bitcoin, con il
 * logo e il colore della moneta (cripto.js, gli stessi del ticker nel
 * piede); una moneta che l'elenco non conosce cade d'oro con la sua sigla.
 * Il bonus fa cadere monete d'oro con la stella.
 *
 * COSA NON FA: non copre i tasti (la pioggia non prende il mouse: solo
 * l'avviso si tocca), non resta (si chiude da sola dopo la durata del
 * modulo, o con la X), e non fa rumore a chi non vuole: il suono lo spegne
 * il modulo per tutti, o il giocatore per se' dall'avviso (preferenze.js), e
 * i browser lo fanno partire solo dopo il primo tocco sulla pagina. Chi ha
 * chiesto al sistema meno animazioni (prefers-reduced-motion) vede solo
 * l'avviso, senza pioggia.
 *
 * PER PROVARLI nell'anteprima del backoffice: &effetto=btc (una sigla) o
 * &effetto=bonus nell'indirizzo, e la festa parte da sola all'apertura.
 */
const ORO = {chiaro: '#fff2b0', scuro: '#f5c542', testo: '#3a2300'};

/** Chi ha chiesto al sistema meno animazioni. */
function menoMovimento() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Un colore del marchio schiarito, per la faccia della moneta. */
function chiarisci(hex) {
  const n = parseInt(String(hex).slice(1), 16);
  if (!Number.isFinite(n)) return ORO.chiaro;
  const c = (v) => Math.round(v + (255 - v) * 0.55).toString(16).padStart(2, '0');
  return '#' + c((n >> 16) & 255) + c((n >> 8) & 255) + c(n & 255);
}

/** Le monete di una pioggia: posizione, misura, durata e ritardo a caso, dentro la durata del modulo. */
function monete(quante, durata, aspetto) {
  const lista = [];
  for (let i = 0; i < quante; i++) {
    const s = 22 + Math.round(Math.random() * 30);
    lista.push(Object.assign({
      k: i, x: Math.round(Math.random() * 98), s,
      d: (durata * 0.6 + Math.random() * durata * 0.5).toFixed(2),
      r: (Math.random() * durata * 0.45).toFixed(2),
      giro: Math.random() > 0.5 ? 1 : -1,
    }, aspetto));
  }
  return lista;
}

/** Il tintinnio: n note corte, sintetizzate sul momento. Senza audio, niente. */
let contesto = null;
function tintinnio(n) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    contesto = contesto || new AC();
    if (contesto.state === 'suspended' && typeof contesto.resume === 'function') contesto.resume();
    const t0 = contesto.currentTime;
    for (let i = 0; i < n; i++) {
      const o = contesto.createOscillator();
      const g = contesto.createGain();
      o.type = 'triangle';
      const f = 1800 + Math.random() * 1400;
      const t = t0 + i * 0.09;
      o.frequency.setValueAtTime(f, t);
      o.frequency.exponentialRampToValueAtTime(f * 0.6, t + 0.25);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.18, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      o.connect(g);
      g.connect(contesto.destination);
      o.start(t);
      o.stop(t + 0.32);
    }
  } catch (e) { /* senza audio si vede solo la pioggia */ }
}

export default function Effetti() {
  const {t} = useTranslation();
  const [scena, setScena] = React.useState(null);
  const [suono, setSuono] = React.useState(suonoEffetti());
  const timer = React.useRef(null);
  const suonoRef = React.useRef(suono);
  suonoRef.current = suono;

  const festa = React.useCallback((tipo, dettaglio) => {
    const conf = effetti();
    if (tipo === 'deposito' && !conf.deposito) return;
    if (tipo === 'bonus' && !conf.bonus) return;
    const telefono = typeof window !== 'undefined' && window.innerWidth < 768;
    const quante = menoMovimento() ? 0 : Math.round(conf.quante / (telefono ? 2 : 1));
    let aspetto;
    let titolo;
    let testo;
    if (tipo === 'bonus') {
      aspetto = Object.assign({simbolo: '★'}, ORO);
      titolo = conf.titoloBonus || t('Bonus attivato!');
      testo = dettaglio.importo ? t('{{importo}} di bonus sul conto. Buon gioco!', {importo: scriviImporto(dettaglio.importo, dettaglio.valuta)}) : '';
    } else {
      const sigla = String(dettaglio.moneta || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      const nota = CRIPTO.find((m) => m.sigla === sigla);
      aspetto = nota
        ? {icona: iconaCripto(sigla), simbolo: '', chiaro: chiarisci(nota.colore), scuro: nota.colore, testo: '#fff'}
        : Object.assign({simbolo: sigla ? sigla.slice(0, 4) : '¤'}, ORO);
      titolo = conf.titoloDeposito || t('Deposito arrivato');
      const cripto = dettaglio.importoCripto ? `${dettaglio.importoCripto} ${sigla}` : '';
      const euro = dettaglio.importo ? scriviImporto(dettaglio.importo, dettaglio.valuta) : '';
      testo = cripto && euro ? t('{{cripto}} accreditati: {{importo}} sul saldo. Buon gioco!', {cripto, importo: euro})
        : euro ? t('{{importo}} accreditati sul saldo. Buon gioco!', {importo: euro})
        : cripto ? t('{{cripto}} accreditati sul saldo. Buon gioco!', {cripto}) : '';
    }
    if (conf.suono && suonoRef.current) tintinnio(tipo === 'bonus' ? 9 : 5);
    clearTimeout(timer.current);
    setScena({tipo, titolo, testo, colore: aspetto.scuro, simbolo: aspetto.simbolo || '★', icona: aspetto.icona, monete: monete(quante, conf.durata, aspetto)});
    timer.current = setTimeout(() => setScena(null), conf.durata * 1000 + 1200);
  }, [t]);

  React.useEffect(() => {
    const alDeposito = (e) => festa('deposito', (e && e.detail) || {});
    const alBonus = (e) => festa('bonus', (e && e.detail) || {});
    window.addEventListener(EVENTO_DEPOSITO, alDeposito);
    window.addEventListener(EVENTO_BONUS, alBonus);
    return () => {
      window.removeEventListener(EVENTO_DEPOSITO, alDeposito);
      window.removeEventListener(EVENTO_BONUS, alBonus);
    };
  }, [festa]);
  /* La festa in corso si ferma solo quando il componente se ne va: non quando si ridisegna. */
  React.useEffect(() => () => clearTimeout(timer.current), []);

  /* La prova dall'anteprima del backoffice: &effetto=btc o &effetto=bonus. */
  React.useEffect(() => {
    if (!inAnteprima()) return undefined;
    const prova = new URLSearchParams(window.location.search).get('effetto');
    if (!prova) return undefined;
    const avvio = setTimeout(() => {
      if (prova === 'bonus') festa('bonus', {importo: 50, valuta: 'EUR'});
      else festa('deposito', {moneta: prova, importoCripto: '0.0016', importo: 100, valuta: 'EUR'});
    }, 800);
    return () => clearTimeout(avvio);
  }, [festa]);

  const alternaSuono = () => {
    const nuovo = !suono;
    setSuono(nuovo);
    impostaSuonoEffetti(nuovo);
  };

  if (!scena) return null;
  return (
    <div className={'eff eff-' + scena.tipo} data-testid={'effetti'}>
      {scena.monete.length > 0 && (
        <div className={'eff-pioggia'} aria-hidden={'true'}>
          {scena.monete.map((m) => (
            <span key={m.k} className={'eff-moneta'} style={{
              left: m.x + '%', width: m.s, height: m.s, fontSize: Math.round(m.s * 0.55),
              animationDuration: m.d + 's', animationDelay: m.r + 's',
              '--eff-chiaro': m.chiaro, '--eff-scuro': m.scuro, '--eff-testo': m.testo, '--eff-giro': m.giro,
            }}>
              {m.icona ? <img src={m.icona} alt={''}/> : m.simbolo}
            </span>
          ))}
        </div>
      )}
      <div className={'eff-avviso'} role={'status'} aria-live={'polite'} style={{'--eff-colore': scena.colore}}>
        <span className={'eff-avviso-moneta'} aria-hidden={'true'}>
          {scena.icona ? <img src={scena.icona} alt={''}/> : scena.simbolo}
        </span>
        <span className={'eff-avviso-testi'}>
          <b>{scena.titolo}</b>
          {scena.testo && <small>{scena.testo}</small>}
        </span>
        {effetti().suono && (
          <button type={'button'} className={'eff-tasto'} onClick={alternaSuono} aria-pressed={suono}
            aria-label={suono ? t('Spegni il suono degli effetti') : t('Accendi il suono degli effetti')}
            title={suono ? t('Spegni il suono degli effetti') : t('Accendi il suono degli effetti')}>
            {suono ? '🔊' : '🔇'}
          </button>
        )}
        <button type={'button'} className={'eff-tasto'} onClick={() => setScena(null)} aria-label={t('Chiudi')}>✕</button>
      </div>
    </div>
  );
}
